import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, type StripeError } from '@stripe/stripe-js'
import { ArrowLeft, Loader2, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { TrialAlreadyUsedError, createPaymentIntent, startTrialWithRetry } from '@/api/stripe'
import { RECEPTIONNISTES, effacerReceptionniste, montantCad, nomReceptionniste, type Receptionniste } from '@/lib/receptionniste'
import { paiement } from '@/lib/analytics/inscription'
import { defaultLocale } from '@/lib/locales'

const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null

type BillingPeriod = 'monthly' | 'yearly'
type Plan = 'starter' | 'pro' | 'solo' | 'team' | 'business' | 'premium'

const SUPPORTED_PLANS: ReadonlyArray<Plan> = [
  'starter',
  'pro',
  'solo',
  'team',
  'business',
  'premium',
]

function isBilling(value: string | null): value is BillingPeriod {
  return value === 'monthly' || value === 'yearly'
}
function isPlan(value: string | null): value is Plan {
  return value != null && (SUPPORTED_PLANS as ReadonlyArray<string>).includes(value)
}

const PLAN_LABEL: Record<Plan, string> = {
  // Legacy
  starter: 'Starter',
  pro: 'Pro',
  // Nouvelle grille
  solo: 'Solo',
  team: 'Team',
  business: 'Business',
  premium: 'Premium',
}

import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { gaClientId } from '@/lib/analytics/ga'

export function CheckoutPage() {
  const { t } = useTranslation()
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const [searchParams] = useSearchParams()
  useDocumentTitle(t('checkout.title', 'Paiement'))

  const planParam = searchParams.get('plan')
  const billingParam = searchParams.get('billing')
  const isTrialRequested = searchParams.get('trial') === '1'
  const receptionniste = (() => {
    const slug = searchParams.get('receptionist')
    return slug && slug in RECEPTIONNISTES ? RECEPTIONNISTES[slug as keyof typeof RECEPTIONNISTES] : null
  })()
  // Fallback : 'team' (plan recommandé de la nouvelle grille) si pas de param valide.
  const plan: Plan = isPlan(planParam) ? planParam : 'team'
  const billing: BillingPeriod = isBilling(billingParam) ? billingParam : 'monthly'

  const intentQuery = useQuery({
    queryKey: ['payment-intent', plan, billing, isTrialRequested, receptionniste?.slug ?? ''],
    queryFn: () =>
      createPaymentIntent({
        plan,
        billing_period: billing,
        is_trial: isTrialRequested,
        ai_pack: receptionniste?.slug,
        ga_client_id: gaClientId(),
      }),
    staleTime: Infinity,
    retry: false,
  })

  // Trial réellement appliqué côté backend (false si l'user en a déjà eu un).
  const trialApplied = intentQuery.data?.trial_applied === true
  // Montant à afficher dans le récap : 0 en trial, sinon montant immédiat.
  const displayAmount = trialApplied ? 0 : intentQuery.data?.amount

  return (
    <CheckoutShell
      plan={plan}
      billing={billing}
      amount={displayAmount}
      currency={intentQuery.data?.currency}
      trialApplied={trialApplied}
      receptionniste={receptionniste}
      trialEnd={intentQuery.data?.trial_end ?? null}
      amountAfterTrial={intentQuery.data?.amount_after_trial}
      isTrialRequested={isTrialRequested}
    >
      {!stripePromise ? (
        <ErrorState message="VITE_STRIPE_PUBLISHABLE_KEY manquant dans .env.local" />
      ) : intentQuery.isLoading ? (
        <PaymentFormSkeleton />
      ) : intentQuery.isError || !intentQuery.data?.client_secret ? (
        // L'appel passe par axios : le corps de la réponse est dans `response.data`.
        (intentQuery.error as { response?: { data?: { code?: string } } } | null)?.response?.data?.code === 'subscription_exists' ? (
          // Le compte a déjà un abonnement : réessayer ne servirait à rien,
          // on l'envoie changer de plan là où ça se fait.
          <AbonnementExistant locale={locale} />
        ) : (
          // Le texte du serveur n'existe qu'en français et parle technique :
          // la personne lit une phrase dans sa langue, avec le bouton Réessayer.
          <ErrorState message={t('checkout.errorInit')} onRetry={() => intentQuery.refetch()} />
        )
      ) : (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: intentQuery.data.client_secret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#0A192F',
                fontFamily: 'Inter, system-ui, sans-serif',
                borderRadius: '8px',
                spacingUnit: '4px',
              },
            },
            locale: locale === 'fr' ? 'fr-CA' : 'en',
          }}
        >
          <CheckoutForm
            amount={intentQuery.data.amount}
            currency={intentQuery.data.currency}
            locale={locale}
            trialApplied={trialApplied}
            intentType={intentQuery.data.intent_type}
          />
        </Elements>
      )}
    </CheckoutShell>
  )
}

interface ShellProps {
  children: React.ReactNode
  plan: Plan
  billing: BillingPeriod
  amount?: number
  currency?: string
  trialApplied: boolean
  trialEnd: number | null
  amountAfterTrial?: number
  isTrialRequested: boolean
  receptionniste?: Receptionniste | null
}

function CheckoutShell({
  children,
  plan,
  billing,
  amount,
  currency,
  trialApplied,
  trialEnd,
  amountAfterTrial,
  receptionniste,
  isTrialRequested,
}: ShellProps) {
  const { t } = useTranslation()
  const { locale = defaultLocale } = useParams<{ locale: string }>()

  const formatMoney = (cents: number, cur: string) => {
    try {
      return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
        style: 'currency',
        currency: cur.toUpperCase(),
      }).format(cents / 100)
    } catch {
      return `${(cents / 100).toFixed(2)} ${cur.toUpperCase()}`
    }
  }

  const formatted = useMemo(() => {
    if (typeof amount !== 'number' || !currency) return null
    return formatMoney(amount, currency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency, locale])

  const formattedAfterTrial = useMemo(() => {
    if (!trialApplied) return null
    if (typeof amountAfterTrial !== 'number' || !currency) return null
    return formatMoney(amountAfterTrial, currency)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialApplied, amountAfterTrial, currency, locale])

  const trialEndDate = useMemo(() => {
    if (!trialEnd) return null
    try {
      return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(trialEnd * 1000))
    } catch {
      return null
    }
  }, [trialEnd, locale])

  // L'user a demandé trial mais le backend a refusé (déjà utilisé) :
  // afficher un avertissement pour transparence.
  // On attend que la réponse backend soit arrivée (amount défini) pour éviter
  // un flash du warning pendant le loading initial où trialApplied=false par défaut.
  const trialDenied = isTrialRequested && typeof amount === 'number' && !trialApplied

  return (
    // html/body ne défilent pas (globals.css) : la colonne de droite gère son
    // propre défilement, sinon le bouton Payer sort de l'écran quand Link se déplie.
    <div className="h-full w-full grid grid-cols-1 md:grid-cols-5 bg-white text-slate-900">
      {/* LEFT — navy minimal */}
      <aside className="hidden md:flex md:col-span-2 flex-col justify-between bg-[#0A192F] text-white p-10">
        <div className="flex items-center gap-3">
          <img
            src="https://sdk.callizy.ca/assets/favicon_callizy.png"
            alt="Callizy"
            className="h-10 w-10"
          />
          <span className="font-bold text-xl">Callizy</span>
        </div>

        <div className="space-y-6">
          <div>
            <p className="text-xs uppercase tracking-wider text-white/40 font-medium mb-1.5">
              {t('checkout.plan', 'Plan')}
            </p>
            <p className="text-lg font-semibold">{PLAN_LABEL[plan]}</p>
            <p className="text-sm text-white/50 mt-0.5">
              {billing === 'yearly'
                ? t('checkout.billingYearly', 'Facturation annuelle')
                : t('checkout.billingMonthly', 'Facturation mensuelle')}
            </p>
            {receptionniste && (
              // Au prix du cycle du forfait : à l'année en annuel, 20 % de moins (2 nov. 2026).
              <div className="mt-3 rounded-lg bg-blue-500/10 border border-blue-400/30 px-3 py-2 text-xs text-blue-100">
                <span className="font-semibold">+ {nomReceptionniste(receptionniste, locale)}</span>
                {' · '}
                {billing === 'yearly'
                  ? t('checkout.receptionist.lineYearly', '{{minutes}} min, {{price}} $ par an', { minutes: receptionniste.minutes, price: montantCad(receptionniste.priceYearlyCad, locale) })
                  : t('checkout.receptionist.line', '{{minutes}} min, {{price}} $ par mois', { minutes: receptionniste.minutes, price: receptionniste.priceCad })}
                <span className="block text-blue-200/70 mt-0.5">
                  {trialApplied
                    ? billing === 'yearly'
                      ? t('checkout.receptionist.afterTrialYearly', "Ajoutée à la fin de l'essai, sur votre facture annuelle.")
                      : t('checkout.receptionist.afterTrial', "Ajoutée à la fin de l'essai, sur votre facture mensuelle.")
                    : t('checkout.receptionist.included', 'Incluse dans ce paiement.')}
                </span>
              </div>
            )}
            {trialApplied && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 px-2.5 py-1 text-xs font-medium text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                {t('checkout.trialBadge', 'Essai gratuit 14 jours')}
              </div>
            )}
          </div>

          {trialDenied && (
            <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-xs text-amber-200">
              {t('checkout.trialDenied', 'Vous avez déjà utilisé votre essai gratuit. Cet abonnement sera facturé immédiatement.')}
            </div>
          )}

          {formatted && (
            <div className="border-t border-white/10 pt-6 space-y-2">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-white/60">
                  {trialApplied ? t('checkout.today', "Aujourd'hui") : t('checkout.total', 'Total')}
                </span>
                <span className="text-2xl font-semibold tracking-tight">{formatted}</span>
              </div>
              {trialApplied && formattedAfterTrial && trialEndDate ? (
                <p className="text-xs text-white/40 mt-1">
                  {t(
                    billing === 'yearly' ? 'checkout.thenPerYear' : 'checkout.thenPerMonth',
                    { amount: formattedAfterTrial, date: trialEndDate },
                  )}
                </p>
              ) : (
                <p className="text-xs text-white/40 mt-1">{t('checkout.taxesIncluded', 'CAD · taxes incluses')}</p>
              )}
            </div>
          )}
        </div>

        <Link
          to={`/${locale}/subscribe`}
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>{t('checkout.backToPlans', 'Retour aux plans')}</span>
        </Link>
      </aside>

      {/* RIGHT — form */}
      <main className="md:col-span-3 h-full overflow-y-auto flex justify-center p-6 md:p-10">
        {/* my-auto : centré quand c'est court, jamais coupé en haut quand c'est long */}
        <div className="w-full max-w-md my-auto">{children}</div>
      </main>
    </div>
  )
}

/**
 * Skeleton qui mime la forme du PaymentElement Stripe pour éviter
 * un flash spinner vide pendant la création du PaymentIntent.
 */
function PaymentFormSkeleton() {
  return (
    <div className="space-y-5 animate-pulse">
      {/* Titre + sous-titre */}
      <div>
        <div className="h-6 bg-slate-200 rounded w-2/3 mb-2.5" />
        <div className="h-3.5 bg-slate-200 rounded w-full mb-1.5" />
        <div className="h-3.5 bg-slate-200 rounded w-4/5" />
      </div>

      {/* Tabs de méthode de paiement */}
      <div className="grid grid-cols-2 gap-2.5 pt-2">
        <div className="h-12 bg-slate-100 rounded-lg border border-slate-300" />
        <div className="h-12 bg-slate-100 rounded-lg border border-slate-200" />
      </div>

      {/* Link banner */}
      <div className="h-10 bg-slate-100 rounded-md border border-slate-200" />

      {/* Numéro de carte */}
      <div>
        <div className="h-3 bg-slate-200 rounded w-24 mb-1.5" />
        <div className="h-11 bg-slate-100 rounded-md border border-slate-200" />
      </div>

      {/* Expiration + CVC */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="h-3 bg-slate-200 rounded w-24 mb-1.5" />
          <div className="h-11 bg-slate-100 rounded-md border border-slate-200" />
        </div>
        <div>
          <div className="h-3 bg-slate-200 rounded w-24 mb-1.5" />
          <div className="h-11 bg-slate-100 rounded-md border border-slate-200" />
        </div>
      </div>

      {/* Pays + Code postal */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="h-3 bg-slate-200 rounded w-12 mb-1.5" />
          <div className="h-11 bg-slate-100 rounded-md border border-slate-200" />
        </div>
        <div>
          <div className="h-3 bg-slate-200 rounded w-24 mb-1.5" />
          <div className="h-11 bg-slate-100 rounded-md border border-slate-200" />
        </div>
      </div>

      {/* Texte légal */}
      <div className="space-y-1.5 pt-1">
        <div className="h-3 bg-slate-100 rounded w-full" />
        <div className="h-3 bg-slate-100 rounded w-3/4" />
      </div>

      {/* Bouton submit */}
      <div className="h-11 bg-slate-300 rounded-md mt-2" />
    </div>
  )
}

/** Un compte a au plus un abonnement : le serveur refuse d'en créer un second
 *  (409 `subscription_exists`). Le plan se change depuis la facturation. */
function AbonnementExistant({ locale }: { locale: string }) {
  const { t } = useTranslation()
  return (
    <div className="text-center space-y-4 py-20">
      <p className="text-base font-medium text-slate-900">{t('checkout.subscriptionExists.title')}</p>
      <p className="text-sm text-slate-600">{t('checkout.subscriptionExists.body')}</p>
      <Button asChild className="bg-slate-900 text-white">
        <Link to={`/${locale}/manager?view=billing`}>{t('checkout.subscriptionExists.cta')}</Link>
      </Button>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="text-center space-y-4 py-20">
      <p className="text-sm text-red-600">{message}</p>
      {onRetry && (
        <Button onClick={onRetry} className="bg-slate-900 text-white">
          {t('common.retry', 'Réessayer')}
        </Button>
      )}
    </div>
  )
}

// Stripe.js renvoie ses erreurs en objets simples, pas en Error.
function estErreurStripe(error: unknown): error is StripeError {
  return typeof error === 'object' && error !== null && !(error instanceof Error) && 'type' in error
}

interface Refus {
  titre: string
  conseil?: string
}

// Au-delà de ces délais, la personne a besoin d'une phrase, pas d'un bouton qui
// tourne. Relevé du 23 oct 2026 : « Activation… » 13 s pendant la vérification
// de la banque (AmEx), puis départ ; le 19 et le 21 oct, aucune carte envoyée.
const FORMULAIRE_LENT_MS = 15_000
const ENVOI_LONG_MS = 20_000

/** Le formulaire de carte vient de Stripe : il peut ne jamais s'afficher
 *  (réseau, bloqueur de publicités). Tant qu'il n'est pas prêt, rien à envoyer. */
type EtatFormulaire = 'chargement' | 'pret' | 'erreur'

/** Un message qui apparaît sous le bouton vient à l'écran : sur un téléphone ou une
 *  fenêtre basse, il tombait sous le bord et le bouton semblait ne rien faire
 *  (essai en mode test du 25 oct 2026, carte refusée). Après un refus, Stripe remet
 *  le curseur dans le champ de carte et ramène la page vers le haut : on défile
 *  juste après lui. */
function montrer(el: HTMLElement | null) {
  if (el) setTimeout(() => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 300)
}

interface FormProps {
  amount: number
  currency: string
  locale: string
  trialApplied: boolean
  intentType: 'payment' | 'setup'
}

function CheckoutForm({ amount, currency, locale, trialApplied, intentType }: FormProps) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  // Le refus reste affiché sous le bouton jusqu'au prochain essai : une bulle
  // de 4 s ne suffisait pas, le client repartait sans essayer une autre carte.
  const [refus, setRefus] = useState<Refus | null>(null)
  const [formulaire, setFormulaire] = useState<EtatFormulaire>('chargement')
  const [envoiLong, setEnvoiLong] = useState(false)
  const essai = intentType === 'setup'

  // Formulaire de carte jamais affiché : on le dit au lieu de laisser une zone vide.
  useEffect(() => {
    if (formulaire !== 'chargement') return
    const id = setTimeout(() => {
      setFormulaire('erreur')
      paiement('formulaire_lent', { essai })
    }, FORMULAIRE_LENT_MS)
    return () => clearTimeout(id)
  }, [formulaire, essai])

  const formatted = useMemo(() => {
    try {
      return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(amount / 100)
    } catch {
      return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
    }
  }, [amount, currency, locale])

  const payMutation = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) throw new Error(t('checkout.stripeNotLoaded', 'Stripe non chargé'))
      // Trial: no subscription exists yet, only a SetupIntent (card saved, nothing
      // charged). Once Stripe confirms the card, the server starts the 14-day
      // trial. A 3DS redirect lands on welcome with ?setup_intent=…, which
      // starts it there.
      if (intentType === 'setup') {
        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/${locale}/onboarding/welcome`,
          },
          redirect: 'if_required',
        })
        if (result.error) throw result.error
        if (result.setupIntent?.status === 'succeeded') await startTrialWithRetry(result.setupIntent.id)
        return result.setupIntent
      }

      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${locale}/onboarding/welcome`,
        },
        redirect: 'if_required',
      })
      if (result.error) throw result.error
      return result.paymentIntent
    },
    onMutate: () => {
      setRefus(null)
      setEnvoiLong(false)
      paiement('envoye', { essai })
    },
    onSuccess: (intent) => {
      paiement('accepte', { essai, statut: intent?.status })
      effacerReceptionniste()
      // PaymentIntent.succeeded ou SetupIntent.succeeded → on bascule sur welcome.
      const ok =
        intent &&
        (intent.status === 'succeeded' || intent.status === 'requires_action')
      if (ok || trialApplied) {
        navigate(`/${locale}/onboarding/welcome`, {
          replace: true,
          state: { fromCheckout: true },
        })
      } else {
        toast.error(t('toasts.checkout.pending'))
      }
    },
    onError: (error: unknown) => {
      if (error instanceof TrialAlreadyUsedError) {
        // Trial already used: the same page without ?trial=1. The server now bills
        // right away and the page says "You already used your free trial", never how
        // we know (Michel, 2 Nov 2026).
        paiement('refuse', { essai, motif: 'essai_deja_utilise' })
        const params = new URLSearchParams(window.location.search)
        params.delete('trial')
        window.location.assign(`${window.location.pathname}?${params.toString()}`)
        return
      }
      const e = estErreurStripe(error) ? error : null
      paiement('refuse', { essai, motif: e?.code || e?.type || 'autre' })
      // Champ incomplet ou invalide : Stripe l'écrit déjà sous le champ.
      if (e?.type === 'validation_error') return
      if (e?.code === 'setup_intent_authentication_failure' || e?.code === 'payment_intent_authentication_failure') {
        setRefus({ titre: t('checkout.authFailed'), conseil: t('checkout.authFailedHint') })
      } else if (e?.type === 'card_error') {
        // Le message de Stripe suit la langue de la page et dit le motif
        // quand la banque en donne un (carte expirée, code de sécurité…).
        setRefus({ titre: e.message || t('checkout.paymentDeclined'), conseil: t('checkout.declinedHint') })
      } else {
        const message = e?.message ?? (error instanceof Error ? error.message : undefined)
        setRefus({ titre: message || t('checkout.paymentDeclined') })
      }
    },
  })

  // Envoi qui dure : souvent la fenêtre de la banque (3D Secure), parfois rien.
  // Aucun délai couperait une vraie vérification ; une phrase dit quoi faire.
  const envoiEnCours = payMutation.isPending
  useEffect(() => {
    if (!envoiEnCours) return
    const id = setTimeout(() => {
      setEnvoiLong(true)
      paiement('envoi_long', { essai })
    }, ENVOI_LONG_MS)
    return () => clearTimeout(id)
  }, [envoiEnCours, essai])

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        payMutation.mutate()
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {trialApplied
            ? t('checkout.trialTitle', 'Démarrer votre essai gratuit')
            : t('checkout.paymentTitle', 'Détails du paiement')}
        </h1>
        {trialApplied && (
          <p className="text-sm text-slate-500 mt-1">
            {t('checkout.trialSubtitle', "Aucun montant ne sera prélevé aujourd'hui. Votre carte est capturée pour la facturation après l'essai.")}
          </p>
        )}
      </header>

      <PaymentElement
        options={{ layout: 'tabs' }}
        onReady={() => {
          setFormulaire('pret')
          paiement('formulaire_pret', { essai })
        }}
        onLoadError={(event) => {
          setFormulaire('erreur')
          paiement('formulaire_erreur', { essai, motif: event.error.code || event.error.type })
        }}
      />

      {formulaire === 'erreur' && (
        <div ref={montrer} role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm">
          <p className="font-medium text-red-700">{t('checkout.cardFormMissing')}</p>
          <p className="mt-1 text-red-600">{t('checkout.cardFormMissingHint')}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 font-medium text-red-700 underline underline-offset-2"
          >
            {t('checkout.reload')}
          </button>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || !elements || formulaire !== 'pret' || payMutation.isPending}
        className="w-full h-12 bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-lg font-semibold text-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
      >
        {payMutation.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{trialApplied ? t('checkout.activating', 'Activation…') : t('checkout.paying', 'Paiement…')}</span>
          </>
        ) : (
          <>
            <Lock className="h-3.5 w-3.5" />
            <span>
              {trialApplied
                ? t('checkout.startTrial', "Démarrer l'essai gratuit")
                : t('checkout.pay', { amount: formatted })}
            </span>
          </>
        )}
      </Button>

      {payMutation.isPending && envoiLong && (
        <div ref={montrer} role="status" className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          <p>{t('checkout.stillWorking')}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 font-medium text-slate-900 underline underline-offset-2"
          >
            {t('checkout.reload')}
          </button>
        </div>
      )}

      {refus && (
        <div ref={montrer} role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm">
          <p className="font-medium text-red-700">{refus.titre}</p>
          {refus.conseil && <p className="mt-1 text-red-600">{refus.conseil}</p>}
        </div>
      )}

      {/* Les liens légaux suivent la locale : codés en dur sur /fr/, un
          anglophone tombait sur des conditions en français au moment de
          payer — le pire endroit pour perdre la langue. */}
      <p className="text-xs text-slate-400 text-center leading-relaxed">
        {t('checkout.legalPrefix', 'En payant, vous acceptez les')}{' '}
        <a
          href={`https://callizy.ca/${locale}/legal/terms`}
          className="underline hover:text-slate-600"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('checkout.legalTerms', "conditions d'utilisation")}
        </a>{' '}
        {t('checkout.legalAnd', 'et la')}{' '}
        <a
          href={`https://callizy.ca/${locale}/legal/privacy`}
          className="underline hover:text-slate-600"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('checkout.legalPrivacy', 'politique de confidentialité')}
        </a>
        .
      </p>
    </form>
  )
}
